import React, { memo, useCallback } from "react";
import {
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  IconButton,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import colors from "app/colors";
import {
  InfoIcon,
  ManageAccountsIcon,
  GearIcon,
  PencilOutlineIcon,
  PersonCircleIcon,
  AdminUserTypeIcon,
} from "modules/icons";
import { UserRoleType } from "gql/graphql";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import EmailAddress from "modules/_shared/email-address";
import useInternalAdminUsers from "../hooks/use-internal-admin-users";
import InternalAdminUserAttributes from "../user/internal-admin-user-attributes";
import InternalAdminEditUserActive from "../user/internal-admin-edit-user-active";
import InternalAdminEditUserCallout from "../user/internal-admin-edit-user-callout";
import InternalAdminEditUserEmail from "../user/internal-admin-edit-user-email";
import InternalAdminEditUserHideEmail from "../user/internal-admin-edit-user-hide-email";
import InternalAdminEditUserHidePhone from "../user/internal-admin-edit-user-hide-phone";
import InternalAdminEditUserName from "../user/internal-admin-edit-user-name";
import InternalAdminEditUserPhone from "../user/internal-admin-edit-user-phone";
import InternalAdminEditUserAllDistributionGroups from "../user/internal-admin-edit-user-all-distribution-groups";
import InternalAdminEditUserAdmin from "../user/internal-admin-edit-user-admin";
import InternalAdminUserDistributionGroups from "../user/internal-admin-user-distribution-groups";
import InternalAdminEditUserSms from "../user/internal-admin-edit-user-sms";
import InternalAdminEditUserLanguage from "../user/internal-admin-edit-user-language";
import InternalAdminUserOnlineStatus from "../components/internal-admin-user-online-status";
import InternalAdminUserAvatar from "../components/internal-admin-user-avatar";

interface IProps {
  userId: string;
}

const InternalAdminUserDrawer = ({ userId }: IProps) => {
  const { openModal } = useOverlay();

  const { user } = useInternalAdminUsers(userId);

  const onEditUserAttributesClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.INTERNAL_ADMIN_EDIT_USER_ATTRIBUTES,
      id: userId,
    });
  }, [openModal, userId]);

  if (!user) {
    return null;
  }

  return (
    <DrawerContent>
      <DrawerCloseButton left={8} top={8} color={colors.gray30} size="lg" />
      <DrawerHeader pt={8}>
        <Center fontFamily="Outfit" fontSize={26} fontWeight={700}>
          User Profile
        </Center>
      </DrawerHeader>
      <DrawerBody p={0}>
        <Stack spacing={8} px={8}>
          <VStack spacing={4} w="full" alignItems="flex-start">
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.black}
            >
              <InfoIcon boxSize={5} />
              <Text fontWeight={600} fontSize={18}>
                User Info
              </Text>
            </HStack>
            <HStack w="full" spacing={4}>
              <InternalAdminUserAvatar
                avatarUrl={user.avatarUrl}
                active={user.active}
                firstName={user.firstName}
                lastName={user.lastName}
                roleType={user.roleType}
                size="xl"
              />
              <VStack w="full" alignItems="flex-start">
                <InternalAdminEditUserName user={user} />
                {user.roleType === UserRoleType.Admin && (
                  <AdminUserTypeIcon fill={colors.gray50} />
                )}
                <InternalAdminUserOnlineStatus user={user} />
              </VStack>
            </HStack>
            <VStack
              w="full"
              alignItems="flex-start"
              divider={<Divider />}
              spacing={0}
            >
              <HStack spacing={0} w="full" minH="48px">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  organization id
                </Text>
                <Text>{user.organizationId}</Text>
              </HStack>
              <HStack spacing={0} w="full" minH="48px">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  id
                </Text>
                <Text>{user.id}</Text>
              </HStack>
              <HStack spacing={0} w="full" minH="48px">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Username
                </Text>
                <InternalAdminEditUserCallout user={user} />
              </HStack>
              <HStack spacing={0} w="full" minH="48px">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Synco Email
                </Text>
                <EmailAddress id={user.id} fontSize="14" truncate />
              </HStack>
              <HStack spacing={0} w="full" minH="48px">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Mobile
                </Text>
                <VStack w="full" alignItems="flex-start" spacing={0}>
                  <InternalAdminEditUserPhone user={user} />
                  <InternalAdminEditUserHidePhone user={user} />
                </VStack>
              </HStack>
              <HStack spacing={0} w="full">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Email
                </Text>
                <VStack w="full" alignItems="flex-start" spacing={0}>
                  <InternalAdminEditUserEmail user={user} />
                  <InternalAdminEditUserHideEmail user={user} />
                </VStack>
              </HStack>
            </VStack>
            <VStack w="full" alignItems="flex-start" spacing={0}>
              <HStack w="full">
                <PersonCircleIcon boxSize={6} />
                <Text fontWeight={600} fontSize={18}>
                  User Attributes
                </Text>
                <Spacer />
                <IconButton
                  aria-label="Edit user attributes"
                  icon={<PencilOutlineIcon boxSize={5} fill={colors.blue} />}
                  variant="ghost"
                  size="xxs"
                  onClick={onEditUserAttributesClicked}
                />
              </HStack>
              <Divider borderColor={colors.black} />
              <InternalAdminUserAttributes user={user} />
            </VStack>
            <InternalAdminEditUserActive user={user} />
          </VStack>

          <VStack w="full" alignItems="flex-start">
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.black}
            >
              <GearIcon boxSize={5} />
              <Text fontWeight={600} fontSize={18}>
                Settings
              </Text>
            </HStack>
            <HStack spacing={0} w="full">
              <Text
                minW={140}
                color={colors.blackSecondary}
                fontWeight={700}
                fontSize={14}
              >
                Language
              </Text>
              <InternalAdminEditUserLanguage user={user} />
            </HStack>
            <Divider />
            <HStack spacing={0} w="full" py={2}>
              <Text
                minW={140}
                color={colors.blackSecondary}
                fontWeight={700}
                fontSize={14}
              >
                SMS Delivery
              </Text>
              <Text color={colors.gray50} fontWeight={500} fontSize={14}>
                Deliver messages via SMS
              </Text>
              <Spacer />
              <InternalAdminEditUserSms user={user} />
            </HStack>
          </VStack>

          <VStack w="full" alignItems="flex-start">
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.black}
            >
              <ManageAccountsIcon boxSize={5} />
              <Text fontWeight={600} fontSize={18}>
                Advanced Settings
              </Text>
            </HStack>
            <VStack spacing={0} w="full" alignItems="flex-start">
              <HStack spacing={0} w="full">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Make an Admin
                </Text>
                <Spacer />
                <InternalAdminEditUserAdmin user={user} />
              </HStack>
              <Text fontSize={14} fontWeight={400} color={colors.grayMain}>
                Admins can add and edit users, groups, and tags
              </Text>
            </VStack>
            <Divider />
            <VStack spacing={0} w="full" alignItems="flex-start">
              <HStack spacing={0} w="full">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Add to all Groups
                </Text>
                <Spacer />
                <InternalAdminEditUserAllDistributionGroups user={user} />
              </HStack>
              <Text fontSize={14} fontWeight={400} color={colors.grayMain}>
                Automatically add this user to every group in org
              </Text>
            </VStack>
          </VStack>

          <InternalAdminUserDistributionGroups user={user} />
        </Stack>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(InternalAdminUserDrawer);
